import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Plus, LogOut, Send, Download, Trash2, Pencil, ShoppingCart, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  borrador: { label: 'Borrador', color: 'bg-gray-100 text-gray-800' },
  enviado: { label: 'Enviado', color: 'bg-blue-100 text-blue-800' },
  aceptado: { label: 'Aceptado', color: 'bg-green-100 text-green-800' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800' },
  expirado: { label: 'Expirado', color: 'bg-yellow-100 text-yellow-800' }
};

export default function AdminQuotes({ user, setUser }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/admin/login'); return; }
    fetchQuotes();
  }, [user, navigate]);

  const fetchQuotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/quotes`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setQuotes(data.quotes || []);
    } catch (e) { toast.error('Error al cargar presupuestos'); }
    finally { setLoading(false); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${BACKEND_URL}/api/admin/quotes/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) { toast.success('Eliminado'); fetchQuotes(); }
  };

  const downloadPDF = async (id) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${BACKEND_URL}/api/admin/quotes/${id}/pdf`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    const data = await res.json();
    if (data.success) {
      const bytes = atob(data.pdf);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `presupuesto-${data.quote_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const sendEmail = async (id) => {
    if (!window.confirm('¿Enviar este presupuesto por email al cliente?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${BACKEND_URL}/api/admin/quotes/${id}/send`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) { toast.success('Presupuesto enviado por email'); fetchQuotes(); }
    else { const err = await res.json(); toast.error(err.detail || 'Error al enviar'); }
  };

  const updateStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    await fetch(`${BACKEND_URL}/api/admin/quotes/${id}`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchQuotes();
  };

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); navigate('/'); };

  const filtered = filterStatus === 'all' ? quotes : quotes.filter(q => q.status === filterStatus);
  const stats = {
    total: quotes.length,
    borrador: quotes.filter(q => q.status === 'borrador').length,
    enviado: quotes.filter(q => q.status === 'enviado').length,
    aceptado: quotes.filter(q => q.status === 'aceptado').length,
    total_value: quotes.filter(q => q.status !== 'rechazado').reduce((s, q) => s + q.total, 0)
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-quotes-page">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white"><LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion</Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6 overflow-x-auto">
            <Link to="/admin" className="text-gray-600 hover:text-black whitespace-nowrap">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black whitespace-nowrap">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black whitespace-nowrap">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black whitespace-nowrap">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black whitespace-nowrap">Cupones</Link>
            <Link to="/admin/quotes" className="font-medium text-black border-b-2 border-black pb-2 whitespace-nowrap">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black whitespace-nowrap">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black whitespace-nowrap">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black whitespace-nowrap">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black whitespace-nowrap">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black whitespace-nowrap">Galeria</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Presupuestos</h2>
          <Button data-testid="create-quote-btn" onClick={() => { setEditingQuote(null); setShowForm(true); }} className="bg-black text-white">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Presupuesto
          </Button>
        </div>

        {showForm && (
          <QuoteForm
            quote={editingQuote}
            onClose={() => { setShowForm(false); setEditingQuote(null); }}
            onSuccess={() => { setShowForm(false); setEditingQuote(null); fetchQuotes(); }}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 border-2 border-black"><p className="text-sm text-gray-600">Total</p><p className="text-2xl font-bold">{stats.total}</p></div>
          <div className="bg-white p-4 border-2 border-gray-300"><p className="text-sm text-gray-600">Borradores</p><p className="text-2xl font-bold">{stats.borrador}</p></div>
          <div className="bg-white p-4 border-2 border-blue-300"><p className="text-sm text-gray-600">Enviados</p><p className="text-2xl font-bold text-blue-600">{stats.enviado}</p></div>
          <div className="bg-white p-4 border-2 border-green-300"><p className="text-sm text-gray-600">Aceptados</p><p className="text-2xl font-bold text-green-600">{stats.aceptado}</p></div>
          <div className="bg-white p-4 border-2 border-black"><p className="text-sm text-gray-600">Valor Total</p><p className="text-2xl font-bold">{stats.total_value.toFixed(2)}&euro;</p></div>
        </div>

        {/* Filter */}
        <div className="mb-4">
          <select data-testid="filter-status" className="border-2 border-gray-300 rounded p-2 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-black">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left p-4 text-sm">N. Presupuesto</th>
                  <th className="text-left p-4 text-sm">Cliente</th>
                  <th className="text-left p-4 text-sm">Total</th>
                  <th className="text-left p-4 text-sm">Estado</th>
                  <th className="text-left p-4 text-sm">Fecha</th>
                  <th className="text-right p-4 text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />No hay presupuestos
                  </td></tr>
                ) : filtered.map((q) => (
                  <tr key={q.id} className="border-b hover:bg-gray-50" data-testid={`quote-row-${q.quote_number}`}>
                    <td className="p-4 font-mono font-bold text-sm">{q.quote_number}</td>
                    <td className="p-4">
                      <p className="font-medium text-sm">{q.client_name}</p>
                      <p className="text-xs text-gray-500">{q.client_email}</p>
                    </td>
                    <td className="p-4 font-bold">{q.total.toFixed(2)}&euro;</td>
                    <td className="p-4">
                      <select className={`text-xs font-medium px-2 py-1 rounded border-0 ${STATUS_CONFIG[q.status]?.color || ''}`}
                        value={q.status} onChange={(e) => updateStatus(q.id, e.target.value)}>
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-sm text-gray-600">{new Date(q.created_at).toLocaleDateString('es-ES')}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" title="Editar" onClick={() => { setEditingQuote(q); setShowForm(true); }}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" title="Descargar PDF" onClick={() => downloadPDF(q.id)}><Download className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" title="Enviar por email" onClick={() => sendEmail(q.id)}><Send className="w-4 h-4" /></Button>
                        {q.status === 'aceptado' && !q.converted_order_id && (
                          <Button variant="ghost" size="sm" title="Convertir en pedido" onClick={() => { setEditingQuote(q); setShowForm('convert'); }}><ShoppingCart className="w-4 h-4 text-green-600" /></Button>
                        )}
                        <Button variant="ghost" size="sm" title="Eliminar" onClick={() => deleteQuote(q.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuoteForm({ quote, onClose, onSuccess }) {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: quote?.client_name || '',
    client_email: quote?.client_email || '',
    client_phone: quote?.client_phone || '',
    client_company: quote?.client_company || '',
    items: quote?.items || [],
    apply_iva: quote?.apply_iva !== false,
    iva_percentage: quote?.iva_percentage || 21,
    shipping_cost: quote?.shipping_cost || 0,
    notes: quote?.notes || '',
    payment_conditions: quote?.payment_conditions || '',
    delivery_timeframe: quote?.delivery_timeframe || '',
    valid_until: quote?.valid_until || '',
    status: quote?.status || 'borrador'
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/products`);
      const data = await res.json();
      // API returns array directly, not {products: [...]}
      setProducts(Array.isArray(data) ? data : (data.products || []));
    } catch (e) { console.error(e); }
  };

  const addProduct = (product) => {
    const exists = form.items.find(i => i.product_id === product.id);
    if (exists) {
      setForm(prev => ({
        ...prev,
        items: prev.items.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i)
      }));
    } else {
      setForm(prev => ({
        ...prev,
        items: [...prev.items, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.price, discount: 0, variant: null, is_custom: false }]
      }));
    }
    setShowSearch(false);
    setSearchTerm('');
  };

  const addCustomItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { product_id: null, product_name: '', quantity: 1, unit_price: 0, discount: 0, variant: null, is_custom: true }]
    }));
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const removeItem = (idx) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  // Calculations — prices are VAT-INCLUSIVE (España B2C). IVA is extracted from the line totals, not added on top.
  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 0), 0);
  const totalDiscount = form.items.reduce((s, i) => {
    const lineTotal = (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 0);
    return s + lineTotal * ((parseFloat(i.discount) || 0) / 100);
  }, 0);
  const afterDiscount = subtotal - totalDiscount;
  const ivaPct = parseFloat(form.iva_percentage) || 0;
  const baseSinIva = form.apply_iva && ivaPct > 0 ? afterDiscount / (1 + ivaPct / 100) : afterDiscount;
  const ivaAmount = form.apply_iva ? afterDiscount - baseSinIva : 0;
  const shippingCost = parseFloat(form.shipping_cost) || 0;
  const total = afterDiscount + shippingCost;

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email) { toast.error('Nombre y email del cliente son obligatorios'); return; }
    if (form.items.length === 0) { toast.error('Añade al menos un producto o línea libre'); return; }
    const incompleteCustom = form.items.find(i => (i.is_custom || !i.product_id) && (!i.product_name || !i.product_name.trim()));
    if (incompleteCustom) { toast.error('Todas las líneas libres deben tener una descripción'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = quote ? `${BACKEND_URL}/api/admin/quotes/${quote.id}` : `${BACKEND_URL}/api/admin/quotes`;
      const method = quote ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { toast.success(quote ? 'Presupuesto actualizado' : 'Presupuesto creado'); onSuccess(); }
      else { const err = await res.json(); toast.error(err.detail || 'Error'); }
    } catch (e) { toast.error('Error de conexion'); }
    finally { setSaving(false); }
  };

  const handleConvert = async () => {
    if (!window.confirm('¿Convertir este presupuesto en pedido?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/quotes/${quote.id}/convert-to-order`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: 'España', shipping_cost: 0 })
      });
      if (res.ok) { toast.success('Pedido creado desde presupuesto'); onSuccess(); }
      else { const err = await res.json(); toast.error(err.detail || 'Error'); }
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="quote-form-modal">
      <div className="bg-white border-2 border-black w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b-2 border-black px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            {quote ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </h3>
          <div className="flex gap-2">
            {quote?.status === 'aceptado' && !quote?.converted_order_id && (
              <Button onClick={handleConvert} className="bg-green-600 text-white" disabled={saving}>
                <ShoppingCart className="w-4 h-4 mr-2" /> Convertir en Pedido
              </Button>
            )}
            <Button onClick={onClose} variant="outline">Cerrar</Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Client data */}
          <div className="border p-4 rounded">
            <h4 className="font-bold mb-3">Datos del Cliente</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Nombre *</Label><Input data-testid="quote-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required /></div>
              <div><Label>Email *</Label><Input data-testid="quote-client-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} required /></div>
              <div><Label>Telefono</Label><Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} /></div>
              <div><Label>Empresa</Label><Input value={form.client_company} onChange={(e) => setForm({ ...form, client_company: e.target.value })} /></div>
            </div>
          </div>

          {/* Products */}
          <div className="border p-4 rounded">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold">Productos</h4>
              <div className="flex gap-2">
                <Button type="button" size="sm" className="bg-black text-white" onClick={() => setShowSearch(!showSearch)}>
                  <Plus className="w-4 h-4 mr-1" /> Añadir producto
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-2 border-black"
                  onClick={addCustomItem}
                  data-testid="add-custom-item-btn"
                  title="Añadir una línea con nombre y precio que tú decides"
                >
                  <Plus className="w-4 h-4 mr-1" /> Línea libre
                </Button>
              </div>
            </div>

            {showSearch && (
              <div className="mb-4 border-2 border-black p-3 rounded bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="product-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar producto..."
                    className="flex-1"
                    autoFocus
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setShowSearch(false); setSearchTerm(''); }}><X className="w-4 h-4" /></Button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredProducts.slice(0, 20).map(p => (
                    <button key={p.id} type="button" onClick={() => addProduct(p)}
                      className="w-full text-left p-2 hover:bg-white rounded flex items-center justify-between text-sm">
                      <span>{p.name} <span className="text-gray-500 text-xs">({p.category})</span></span>
                      <span className="font-bold">{p.price.toFixed(2)}&euro;</span>
                    </button>
                  ))}
                  {filteredProducts.length === 0 && <p className="text-sm text-gray-500 p-2">No se encontraron productos</p>}
                </div>
              </div>
            )}

            {form.items.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Añade productos al presupuesto</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b">
                  <th className="text-left py-2">Producto</th>
                  <th className="text-center py-2 w-20">Cant.</th>
                  <th className="text-right py-2 w-28">P. Unit.</th>
                  <th className="text-center py-2 w-24">Dto. %</th>
                  <th className="text-right py-2 w-28">Total</th>
                  <th className="w-10"></th>
                </tr></thead>
                <tbody>
                  {form.items.map((item, idx) => {
                    const lineTotal = (parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 0);
                    const lineDiscount = lineTotal * ((parseFloat(item.discount) || 0) / 100);
                    const lineNet = lineTotal - lineDiscount;
                    return (
                      <tr key={`quote-line-${item.product_id || idx}-${idx}`} className="border-b">
                        <td className="py-2">
                          {item.is_custom || !item.product_id ? (
                            <Input
                              type="text"
                              placeholder="Descripción de la línea libre"
                              className="text-sm"
                              value={item.product_name}
                              onChange={(e) => updateItem(idx, 'product_name', e.target.value)}
                              data-testid={`custom-item-name-${idx}`}
                            />
                          ) : (
                            item.product_name
                          )}
                        </td>
                        <td className="py-2">
                          <Input type="number" min="1" className="w-20 text-center text-sm" value={item.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} />
                        </td>
                        <td className="py-2">
                          <Input type="number" step="0.01" className="w-28 text-right text-sm" value={item.unit_price}
                            onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td className="py-2">
                          <Input type="number" step="0.5" min="0" max="100" className="w-20 text-center text-sm" value={item.discount || 0}
                            onChange={(e) => updateItem(idx, 'discount', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td className="py-2 text-right">
                          {lineDiscount > 0 ? (
                            <div>
                              <span className="line-through text-gray-400 text-xs">{lineTotal.toFixed(2)}</span>
                              <span className="font-medium ml-1">{lineNet.toFixed(2)}&euro;</span>
                            </div>
                          ) : (
                            <span className="font-medium">{lineTotal.toFixed(2)}&euro;</span>
                          )}
                        </td>
                        <td className="py-2"><button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* IVA + Envío */}
          <div className="border p-4 rounded">
            <h4 className="font-bold mb-3">IVA y Envío</h4>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Aplicar IVA</Label>
                <select className="w-full border rounded p-2 text-sm" value={form.apply_iva ? 'si' : 'no'}
                  onChange={(e) => setForm({ ...form, apply_iva: e.target.value === 'si' })}>
                  <option value="si">Si</option>
                  <option value="no">No (exento)</option>
                </select>
              </div>
              {form.apply_iva && (
                <div>
                  <Label>% IVA</Label>
                  <Input type="number" step="0.5" min="0" value={form.iva_percentage}
                    onChange={(e) => setForm({ ...form, iva_percentage: parseFloat(e.target.value) || 0 })} />
                </div>
              )}
              <div>
                <Label>Coste de envío (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.shipping_cost}
                  onChange={(e) => setForm({ ...form, shipping_cost: parseFloat(e.target.value) || 0 })}
                  data-testid="quote-shipping-cost-input"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Totals preview */}
            <div className="mt-4 bg-gray-50 p-3 rounded text-right space-y-1">
              <p className="text-sm">Subtotal: <span className="font-bold">{subtotal.toFixed(2)}&euro;</span></p>
              {totalDiscount > 0 && <p className="text-sm text-green-600">Descuentos por linea: <span className="font-bold">-{totalDiscount.toFixed(2)}&euro;</span></p>}
              {form.apply_iva && <p className="text-sm">IVA ({form.iva_percentage}%): <span className="font-bold">{ivaAmount.toFixed(2)}&euro;</span></p>}
              {shippingCost > 0 && <p className="text-sm">Envío: <span className="font-bold">{shippingCost.toFixed(2)}&euro;</span></p>}
              <p className="text-lg font-bold border-t pt-2">TOTAL: {total.toFixed(2)}&euro;</p>
            </div>
          </div>

          {/* Conditions */}
          <div className="border p-4 rounded">
            <h4 className="font-bold mb-3">Condiciones y Notas</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Condiciones de pago</Label>
                <Textarea value={form.payment_conditions} onChange={(e) => setForm({ ...form, payment_conditions: e.target.value })}
                  rows={2} placeholder="Ej: Pago 50% anticipado, 50% a la entrega" />
              </div>
              <div>
                <Label>Plazo de entrega</Label>
                <Input value={form.delivery_timeframe} onChange={(e) => setForm({ ...form, delivery_timeframe: e.target.value })}
                  placeholder="Ej: 5-7 dias laborables" />
              </div>
              <div>
                <Label>Validez del presupuesto</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  min={new Date().toISOString().split('T')[0]} />
              </div>
              <div>
                <Label>Estado</Label>
                <select className="w-full border rounded p-2 text-sm" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} placeholder="Notas adicionales para el presupuesto..." />
            </div>
          </div>

          <div className="flex gap-3">
            <Button data-testid="save-quote-btn" type="submit" className="bg-black text-white flex-1" disabled={saving}>
              {saving ? 'Guardando...' : quote ? 'Guardar Cambios' : 'Crear Presupuesto'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
